// Search V2 types

export type SearchContext =
  | "all"
  | "series"
  | "releases"
  | "creators"
  | "bundles";

export type ChannelSearchContext = {
  browseKey: string;
  filters?: Record<string, number>;
};

export type ActiveSearchContext = SearchContext | ChannelSearchContext;

export function isChannelContext(
  ctx: ActiveSearchContext,
): ctx is ChannelSearchContext {
  return typeof ctx === "object" && "browseKey" in ctx;
}

// --- Result items ---

export type SearchSeriesItem = {
  id: number;
  name: string;
  slug: string;
  total_releases: number;
  total_published_releases: number;
  count_gold: number;
  cover_image_url: string;
  artist_name: string;
  releases_count_string: string | null;
  is_favorite?: boolean;
  read_status?: string;
};

export type SearchReleaseItem = {
  id: number;
  key: string;
  slug: string;
  title: string;
  lang_id: string;
  thumbnail_url: string | null;
  comic_name: string;
  comic_slug: string;
  artist_name: string;
  premium_only: boolean;
  is_free?: boolean;
  ppb_price_point?: number | null;
  ppb_sale_price_point?: number | null;
  ppb_gold_price_point?: number | null;
  ppb_gold_sale_price_point?: number | null;
  is_purchased?: boolean;
};

export type SearchCreatorItem = {
  id: number;
  name: string;
  roman_name: string;
  avatar_url: string;
  avatar_small_url: string;
  country_id: string;
  total_favorites: number;
  is_following?: boolean;
};

export type SearchSuggestItem = {
  type: "series" | "creator";
  id: number;
  name: string;
  slug: string;
};

// --- Response shapes ---

export type SearchResultSet<T> = {
  total: number;
  items: T[];
};

// Non-enriched response has id/uuid/display_name; enriched response returns full Bundle shape.
// The enriched items are cast to Bundle in useSearchV2API via mapBundleToBundle.
export type SearchBundleItem = {
  id: number;
  uuid: string;
  display_name: string;
};

export type SearchQueryResponse = {
  semantic: boolean;
  display_order: string[];
  series: SearchResultSet<SearchSeriesItem>;
  releases: SearchResultSet<SearchReleaseItem>;
  creators: SearchResultSet<SearchCreatorItem>;
  bundles: SearchResultSet<SearchBundleItem>;
};

// --- Query params ---

export type SearchQueryParams = {
  q?: string;
  context?: SearchContext;
  p?: number;
  perpage?: number;
  sort?: string;
  sort_duration?: string;
  enrich?: boolean;
  semantic?: boolean;
  comic_type_id?: number;
  comic_genre_id?: number;
  comic_format_id?: number;
  artist_id?: number;
  artist_type_id?: number;
  lang_id?: string[];
  country_id?: string[];
  license_type_id?: number;
  facets?: boolean | Record<string, string[]>;
  crowdfunding?: boolean;
  is_augmented?: boolean;
  tag?: string;
  contributor?: string;
};
